// Silver Sage — Payroll Error Interpreter.
// See mem/preferences/errors-are-for-people.md and the Executive Recovery
// Experience directive. When a payroll proposal fails, we translate the
// backend cause into a Chief-of-Staff-style explanation with an outcome-verb
// next action. We never end on "I couldn't…"; we always end on
// "Here's what we can do next."

export type PayrollNextActionKind =
  | "open_payroll"
  | "complete_setup"
  | "set_pay_rate"
  | "add_payment_method"
  | "create_correction"
  | "refresh_session"
  | "try_again"
  | "review_history";

export interface PayrollNextAction {
  kind: PayrollNextActionKind;
  /** Outcome-verb label. e.g. "Retry Holly's Payroll", "Set Holly's Pay Rate". */
  label: string;
  /** Deep link to the exact record (or the workspace that owns it). */
  href?: string;
  /** When true, the primary button retries the same approval. */
  isRetry?: boolean;
}

export interface InterpretedPayrollError {
  /** Card headline. e.g. "Payroll temporarily unavailable". */
  title: string;
  /** First paragraph — what happened, in one plain sentence naming the subject. */
  what: string;
  /** Reassurance about what did / did not change. */
  changed: string;
  /** Third paragraph — what MAVIS has already done to preserve momentum. */
  reassurance: string;
  /** Primary recommended next step. */
  nextAction: PayrollNextAction;
  /** Optional secondary suggestion — always "or you can…" style. */
  alternate?: PayrollNextAction;
  /** True when the failure looks like a temporary service/network hiccup.
   * These are the ones MAVIS remembers overnight and brings back in the
   * morning brief. */
  transient: boolean;
  /** Raw technical string for "Show Technical Details" and console logs. */
  technical: string;
}

function rawMessage(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;
  if (typeof err === "object") {
    const anyErr = err as { message?: unknown; error?: unknown; details?: unknown; hint?: unknown };
    const parts = [anyErr.message, anyErr.error, anyErr.details, anyErr.hint]
      .filter((x): x is string => typeof x === "string");
    if (parts.length) return parts.join(" — ");
    try { return JSON.stringify(err); } catch { return String(err); }
  }
  return String(err);
}

/**
 * Interpret a payroll-approval failure. `subject` is the instructor's display
 * name (e.g. "Holly"). We never surface raw SQL, HTTP codes, or UUIDs above
 * the fold — those go in the returned `technical` string.
 *
 * `instructorHref` is a deep link to the instructor's record when we know it,
 * so the recovery buttons take Savanna straight there instead of a generic
 * page.
 */
export function interpretPayrollError(
  err: unknown,
  subject: string,
  opts: { instructorHref?: string; periodLabel?: string } = {},
): InterpretedPayrollError {
  const raw = rawMessage(err);
  const m = raw.toLowerCase();
  const who = subject?.trim() || "this instructor";
  const period = opts.periodLabel ? ` ${opts.periodLabel}` : "";
  const unchanged = "Nothing was partially saved, so there's nothing to clean up.";
  const instructorHref = opts.instructorHref || "/admin/instructors";

  // --- Append-only ledger conflict ------------------------------------------
  if (
    m.includes("append-only") ||
    m.includes("append only") ||
    (m.includes("payroll") && (m.includes("cannot be modified") || m.includes("cannot be deleted"))) ||
    m.includes("payroll_ledger_reject_change") ||
    m.includes("payroll_confirmations_reject_change")
  ) {
    return {
      title: "Payroll history is locked",
      what: `I wasn't able to edit ${who}'s${period} payroll because the entry is already recorded and locked for audit safety.`,
      changed: "Nothing on the existing entry was changed.",
      reassurance: `The right move here is a correction entry — I've kept this ready to record as a correction whenever you're ready.`,
      nextAction: {
        kind: "create_correction",
        label: `Record a Correction For ${who}`,
        href: "/admin/payroll",
      },
      alternate: {
        kind: "review_history",
        label: "Review Payroll History First",
        href: "/admin/payroll",
      },
      transient: false,
      technical: raw,
    };
  }

  // --- Duplicate ledger / payment entry -------------------------------------
  if (
    m.includes("duplicate key") ||
    m.includes("already exists") ||
    m.includes("unique constraint") ||
    m.includes("uniqueness violation") ||
    m.includes("immutable_hash") ||
    m.includes("23505")
  ) {
    return {
      title: "This payment is already on file",
      what: `${who} already has an identical payroll entry for${period || " this period"}.`,
      changed: "No duplicate was created.",
      reassurance: `The existing payment is intact and correct — you can review it in payroll history.`,
      nextAction: {
        kind: "review_history",
        label: `Review ${who}'s Payment History`,
        href: "/admin/payroll",
      },
      transient: false,
      technical: raw,
    };
  }

  // --- Amount mismatch / conflicting existing payment -----------------------
  if (
    m.includes("amount mismatch") ||
    m.includes("conflicting payment") ||
    (m.includes("different") && m.includes("amount")) ||
    (m.includes("mismatch") && (m.includes("amount") || m.includes("final_cents")))
  ) {
    return {
      title: "A different amount is already on file",
      what: `${who} already has a payroll entry for${period || " this period"} at a different amount.`,
      changed: unchanged,
      reassurance: `Let's compare the two amounts together before recording anything — I've kept this ready to approve once you've decided which is right.`,
      nextAction: {
        kind: "review_history",
        label: `Compare ${who}'s Payroll Amounts`,
        href: "/admin/payroll",
      },
      transient: false,
      technical: raw,
    };
  }

  // --- Invalid pay period ---------------------------------------------------
  if (
    m.includes("invalid pay period") ||
    m.includes("not in period") ||
    m.includes("outside period") ||
    (m.includes("period") && (m.includes("closed") || m.includes("does not belong") || m.includes("mismatch")))
  ) {
    return {
      title: "This payment belongs in a different period",
      what: `${who}'s pay date falls outside the payroll period you're recording into.`,
      changed: unchanged,
      reassurance: `Once the right period is selected, this payment is ready to record.`,
      nextAction: {
        kind: "open_payroll",
        label: "Choose The Correct Period",
        href: "/admin/payroll",
      },
      transient: false,
      technical: raw,
    };
  }

  // --- Missing pay rate -----------------------------------------------------
  if (
    m.includes("pay rate") ||
    m.includes("pay_rate") ||
    m.includes("hourly_rate") ||
    (m.includes("rate") && m.includes("missing"))
  ) {
    return {
      title: `${who}'s pay rate is missing`,
      what: `I wasn't able to record ${who}'s${period} payroll because there isn't a pay rate on file yet.`,
      changed: unchanged,
      reassurance: `As soon as ${who}'s rate is set, I can record this payment — I've kept the proposal ready for you.`,
      nextAction: {
        kind: "set_pay_rate",
        label: `Set ${who}'s Pay Rate`,
        href: instructorHref,
      },
      alternate: {
        kind: "open_payroll",
        label: "Continue With Other Payroll",
        href: "/admin/payroll",
      },
      transient: false,
      technical: raw,
    };
  }

  // --- Missing payment method ----------------------------------------------
  if (
    m.includes("payment method") ||
    m.includes("payment_method") ||
    (m.includes("zelle") && m.includes("missing"))
  ) {
    return {
      title: `${who}'s payment method is missing`,
      what: `I need to know how ${who} gets paid — Zelle, check, cash, or otherwise — before I can record this.`,
      changed: unchanged,
      reassurance: `Once the method is on file, this payment will be ready to record.`,
      nextAction: {
        kind: "add_payment_method",
        label: `Add ${who}'s Payment Method`,
        href: instructorHref,
      },
      alternate: {
        kind: "open_payroll",
        label: "Continue With Other Payroll",
        href: "/admin/payroll",
      },
      transient: false,
      technical: raw,
    };
  }

  // --- Missing payroll profile / instructor link ---------------------------
  if (
    (m.includes("instructor_id") && (m.includes("null") || m.includes("required") || m.includes("missing"))) ||
    (m.includes("not null") && m.includes("instructor")) ||
    m.includes("payroll profile") ||
    m.includes("no matching instructor") ||
    m.includes("instructor not found")
  ) {
    return {
      title: `${who}'s payroll setup isn't finished`,
      what: `${who}'s instructor profile isn't fully connected to payroll yet, so I can't record a payment.`,
      changed: unchanged,
      reassurance: `Finish the setup and I'll take it from there — the proposal is safely kept.`,
      nextAction: {
        kind: "complete_setup",
        label: `Finish ${who}'s Payroll Setup`,
        href: instructorHref,
      },
      alternate: {
        kind: "open_payroll",
        label: "Continue With Other Payroll",
        href: "/admin/payroll",
      },
      transient: false,
      technical: raw,
    };
  }

  // --- Auth / admin session issue ------------------------------------------
  if (
    m.includes("unauthorized") ||
    m.includes("not authorized") ||
    m.includes("admin only") ||
    m.includes("jwt") ||
    (m.includes("session") && (m.includes("expired") || m.includes("invalid"))) ||
    m.includes("401") ||
    m.includes("403") ||
    m.includes("permission denied") ||
    m.includes("row-level security") ||
    m.includes("rls")
  ) {
    return {
      title: "Your admin sign-in needs a refresh",
      what: `Payroll didn't accept the current sign-in when I tried to record ${who}'s${period} payment.`,
      changed: unchanged,
      reassurance: `A quick refresh brings you back in and I can pick this right back up.`,
      nextAction: {
        kind: "refresh_session",
        label: "Refresh My Sign-In",
      },
      alternate: {
        kind: "try_again",
        label: `Retry ${who}'s Payroll`,
        isRetry: true,
      },
      transient: false,
      technical: raw,
    };
  }

  // --- Temporary edge-function / network outage ----------------------------
  if (
    m.includes("failed to fetch") ||
    m.includes("network") ||
    m.includes("timeout") ||
    m.includes("timed out") ||
    m.includes("502") ||
    m.includes("503") ||
    m.includes("504") ||
    m.includes("edge function") ||
    m.includes("boot") ||
    m.includes("upstream") ||
    m.includes("temporarily")
  ) {
    return {
      title: "Payroll temporarily unavailable",
      what: `I wasn't able to record ${who}'s${period} payroll because the payroll service isn't responding right now.`,
      changed: `The good news is that ${unchanged.toLowerCase()}`,
      reassurance: `I've kept this payment ready to approve as soon as the service is available again — and I'll remind you in the morning if it's still waiting.`,
      nextAction: {
        kind: "try_again",
        label: `Retry ${who}'s Payroll`,
        isRetry: true,
      },
      alternate: {
        kind: "open_payroll",
        label: "Continue Reviewing Payroll",
        href: "/admin/payroll",
      },
      transient: true,
      technical: raw,
    };
  }

  // --- Unknown failure -----------------------------------------------------
  return {
    title: `Payroll didn't finish for ${who}`,
    what: `I wasn't able to record ${who}'s${period} payment. The payroll service turned it down and didn't say why in a way I recognize.`,
    changed: unchanged,
    reassurance: `I've kept the proposal ready — we can retry in a moment, or take a look at what the service reported.`,
    nextAction: {
      kind: "try_again",
      label: `Retry ${who}'s Payroll`,
      isRetry: true,
    },
    alternate: {
      kind: "open_payroll",
      label: "Open Payroll",
      href: "/admin/payroll",
    },
    transient: false,
    technical: raw,
  };
}

export function isPayrollProposal(action_kind?: string, target_kind?: string, target_table?: string): boolean {
  const a = (action_kind || "").toLowerCase();
  const k = (target_kind || target_table || "").toLowerCase();
  return (
    a === "one_time_payment" ||
    a.includes("payroll") ||
    k.includes("payroll") ||
    k === "instructor_one_time_payments" ||
    k === "payroll_ledger"
  );
}
